/* Popover: a light panel for form controls (the composer's Options). Trigger gets aria-haspopup="dialog",
   aria-expanded and aria-controls. Opens on click; focus moves to the first control and Tab cycles inside the
   panel; Esc closes and returns focus to the trigger; an outside click closes it. Menu (Menu.tsx) is the
   sibling for lists of commands. */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { cx } from './cx';

export interface PopoverTriggerProps {
  ref: RefObject<HTMLButtonElement | null>;
  'aria-haspopup': 'dialog';
  'aria-expanded': boolean;
  'aria-controls': string | undefined;
  onClick: () => void;
}

export interface PopoverProps {
  /** Accessible name of the panel. */
  label: string;
  trigger: (props: PopoverTriggerProps) => ReactNode;
  children: ReactNode | ((api: { close: () => void }) => ReactNode);
  align?: 'start' | 'end';
  /** Opens above the trigger when there is room. */
  prefer?: 'below' | 'above';
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

const FOCUSABLE = 'input:not([disabled]),select:not([disabled]),textarea:not([disabled]),button:not([disabled]),a[href],[tabindex]:not([tabindex="-1"])';

export function Popover({ label, trigger, children, align = 'end', prefer = 'below', open: openProp, onOpenChange, className }: PopoverProps) {
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = useCallback((v: boolean) => { if (openProp === undefined) setOpenState(v); onOpenChange?.(v); }, [openProp, onOpenChange]);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const id = useId();
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const close = useCallback((refocus = true) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  }, [setOpen]);

  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    const t = triggerRef.current, p = popRef.current;
    if (!t || !p) return;
    const r = t.getBoundingClientRect();
    const w = p.offsetWidth || 340, h = p.offsetHeight || 300;
    const vw = window.innerWidth, vh = window.innerHeight;
    let left = align === 'end' ? r.right - w : r.left;
    left = Math.max(8, Math.min(left, vw - w - 8));
    const below = r.bottom + 6, above = r.top - h - 6;
    let top = prefer === 'above' ? (above >= 8 ? above : below) : (below + h <= vh - 8 || above < 8 ? below : above);
    top = Math.max(8, Math.min(top, vh - h - 8));
    setPos({ top, left });
  }, [open, align, prefer]);

  useEffect(() => {
    if (!open) return;
    const p = popRef.current;
    const first = p?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? p)?.focus({ preventScroll: true });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent | MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      close(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const a = document.activeElement;
      if (popRef.current?.contains(a) || triggerRef.current?.contains(a) || a === document.body) { e.preventDefault(); close(); }
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open, close]);

  // Focus stays inside while open: Tab past the last control wraps to the first, Shift+Tab before the first wraps
  // to the last. Esc, an outside click or the close callback leave (Esc and close return focus to the trigger).
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const items = Array.from(popRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []).filter((el) => !el.closest('[hidden]'));
    if (!items.length) { e.preventDefault(); return; }
    const a = document.activeElement;
    if (!e.shiftKey && a === items[items.length - 1]) { e.preventDefault(); items[0]!.focus(); }
    else if (e.shiftKey && (a === items[0] || a === popRef.current)) { e.preventDefault(); items[items.length - 1]!.focus(); }
  };

  const onBlur = (e: React.FocusEvent) => {
    const to = e.relatedTarget as Node | null;
    if (!to) return;
    if (popRef.current?.contains(to) || triggerRef.current?.contains(to)) return;
    close(false);
  };

  const props: PopoverTriggerProps = {
    ref: triggerRef,
    'aria-haspopup': 'dialog',
    'aria-expanded': open,
    'aria-controls': open ? id : undefined,
    onClick: () => setOpen(!open),
  };

  const pop = open ? (
    <div
      ref={popRef}
      id={id}
      role="dialog"
      aria-label={label}
      tabIndex={-1}
      className={cx('popover-pop', className)}
      style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999 }}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
    >
      {typeof children === 'function' ? children({ close }) : children}
    </div>
  ) : null;

  return (
    <>
      {trigger(props)}
      {pop && (typeof document !== 'undefined' ? createPortal(pop, document.body) : pop)}
    </>
  );
}
